import React, { useState, useEffect } from "react";
import "./App.css";
import "./AdminDashboard.css";
import "./AdminLogin.css";
import "./PrivacyNotice.css";
import axios from "axios";
import { CheckCircle, Mail, Star, Truck, Phone, Users, Target, Send, AlertCircle, BarChart3, ArrowLeft } from "lucide-react";
import AdminDashboard from "./AdminDashboard";
import AdminLogin from "./AdminLogin";
import PrivacyNotice from "./PrivacyNotice";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Rating Components for 5-star system
const StarRating = ({ value, onChange, labels = [] }) => {
  return (
    <div className="star-rating">
      <div className="stars-container">
        {[...Array(5)].map((_, i) => (
          <button
            key={i}
            type="button"
            className={`star ${i < value ? 'active' : ''}`}
            onClick={() => onChange(i + 1)}
            aria-label={`${i + 1} von 5 Sternen`}
          >
            <Star size={28} fill={i < value ? "currentColor" : "none"} />
          </button>
        ))}
      </div>
      {labels[value - 1] && (
        <div className="rating-label">{labels[value - 1]}</div>
      )}
    </div>
  );
};

// NPS Rating Component for 0-10 scale
const NPSRating = ({ value, onChange }) => {
  return (
    <div className="nps-rating">
      <div className="nps-scale">
        {[...Array(11)].map((_, i) => (
          <button
            key={i}
            type="button"
            className={`nps-button ${value === i ? 'active' : ''}`}
            onClick={() => onChange(i)}
            aria-label={`${i} von 10`}
          >
            {i}
          </button>
        ))}
      </div>
      <div className="nps-labels">
        <span className="nps-label-left">sehr unwahrscheinlich</span>
        <span className="nps-label-right">sehr wahrscheinlich</span>
      </div>
    </div>
  );
};

const SurveyForm = () => {
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    company_name: '',
    job_position: '',
    order_number: '',
    
    // 1. Allgemeine Zufriedenheit
    overall_satisfaction_rating: 0,
    recommendation_rating: null,
    
    // 2. Produkte & Sortiment
    product_quality_comparison_rating: 0,
    product_range_satisfaction: '',
    product_wishes: '',
    
    // 3. Preis & Konditionen
    price_performance_rating: 0,
    delivery_payment_conditions: '',
    
    // 4. Lieferung & Logistik
    delivery_reliability_rating: 0,
    delivery_completeness_rating: 0,
    
    // 5. Service & Kommunikation
    consultation_competence_rating: 0,
    digital_communication_rating: 0,
    
    // 6. Offene Anmerkungen
    keep_suggestions: '',
    improvement_suggestions: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Rating labels for 5-star system
  const satisfactionLabels = ["Sehr unzufrieden", "Unzufrieden", "Neutral", "Zufrieden", "Sehr zufrieden"];
  const qualityComparisonLabels = ["Schlechter", "Etwas schlechter", "Gleichwertig", "Besser", "Deutlich besser"];
  const pricePerformanceLabels = ["Sehr schlecht", "Schlecht", "Durchschnittlich", "Gut", "Sehr gut"];
  const reliabilityLabels = ["Sehr unzuverlässig", "Unzuverlässig", "Durchschnittlich", "Zuverlässig", "Sehr zuverlässig"];
  const completenessLabels = ["Sehr schlecht", "Schlecht", "Durchschnittlich", "Gut", "Sehr gut"];
  const competenceLabels = ["Sehr inkompetent", "Inkompetent", "Durchschnittlich", "Kompetent", "Sehr kompetent"];
  const digitalLabels = ["Sehr unzufrieden", "Unzufrieden", "Neutral", "Zufrieden", "Sehr zufrieden"];

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const validateForm = () => {
    if (!formData.customer_name.trim()) return "Name ist erforderlich";
    if (!formData.customer_email.trim()) return "E-Mail ist erforderlich";
    if (!formData.overall_satisfaction_rating) return "Bitte bewerten Sie die Gesamtzufriedenheit";
    if (formData.recommendation_rating === null) return "Bitte geben Sie eine Weiterempfehlung ab";
    if (!formData.product_quality_comparison_rating) return "Bitte bewerten Sie die Produktqualität";
    if (!formData.product_range_satisfaction) return "Bitte bewerten Sie unser Sortiment";
    if (!formData.price_performance_rating) return "Bitte bewerten Sie das Preis-Leistungs-Verhältnis";
    if (!formData.delivery_payment_conditions) return "Bitte bewerten Sie die Konditionen";
    if (!formData.delivery_reliability_rating) return "Bitte bewerten Sie die Lieferzuverlässigkeit";
    if (!formData.delivery_completeness_rating) return "Bitte bewerten Sie die Liefervollständigkeit";
    if (!formData.consultation_competence_rating) return "Bitte bewerten Sie die Beratungskompetenz";
    if (!formData.digital_communication_rating) return "Bitte bewerten Sie die digitale Kommunikation";
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Convert empty strings to null for optional fields
      const submissionData = { ...formData };
      
      if (submissionData.company_name === '') {
        submissionData.company_name = null;
      }
      if (submissionData.job_position === '') {
        submissionData.job_position = null;
      }
      if (submissionData.order_number === '') {
        submissionData.order_number = null;
      }
      if (submissionData.product_wishes === '') {
        submissionData.product_wishes = null;
      }
      if (submissionData.keep_suggestions === '') {
        submissionData.keep_suggestions = null;
      }
      if (submissionData.improvement_suggestions === '') {
        submissionData.improvement_suggestions = null;
      }
      
      const response = await axios.post(`${API}/survey/submit`, submissionData);
      
      if (response.data.success) {
        setSubmitted(true);
      } else {
        setError('Fehler beim Übermitteln der Umfrage');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setError('Fehler beim Übermitteln der Umfrage. Bitte versuchen Sie es später erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="success-page">
        <div className="success-content">
          <CheckCircle size={80} className="success-icon" />
          <h1>Vielen Dank für Ihr <span className="success-highlight">Feedback</span>!</h1>
          <p>Ihre Bestandskundenumfrage wurde erfolgreich übermittelt und an unser <span className="success-highlight">Service-Team</span> gesendet.</p>
          <p>Wir schätzen Ihre Zeit und Ihr wertvolles Feedback sehr und werden Ihre Anregungen zur kontinuierlichen Verbesserung unserer Leistungen nutzen.</p>
          <button 
            className="new-survey-btn"
            onClick={() => {
              setSubmitted(false);
              setFormData({
                customer_name: '',
                customer_email: '',
                company_name: '',
                job_position: '',
                order_number: '',
                overall_satisfaction_rating: 0,
                recommendation_rating: null,
                product_quality_comparison_rating: 0,
                product_range_satisfaction: '',
                product_wishes: '',
                price_performance_rating: 0,
                delivery_payment_conditions: '',
                delivery_reliability_rating: 0,
                delivery_completeness_rating: 0,
                consultation_competence_rating: 0,
                digital_communication_rating: 0,
                keep_suggestions: '',
                improvement_suggestions: ''
              });
            }}
          >
            Neue Umfrage
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="survey-container">
      <div className="survey-header">
        <div className="company-logo">
          <img 
            src="https://customer-assets.emergentagent.com/job_neukunden-survey/artifacts/jcwk0oru_1_Logo_Eurosill_klein.jpg" 
            alt="Eurosill Logo" 
            className="logo-image"
          />
        </div>
        <div className="header-content">
          <h2>Bestandskundenumfrage</h2>
          <p className="header-description">
            Als geschätzter Bestandskunde möchten wir von Ihnen erfahren, wie zufrieden Sie mit unserer Zusammenarbeit sind. 
            Ihre Meinung hilft uns dabei, unsere Leistungen kontinuierlich zu verbessern und noch besser auf Ihre Bedürfnisse einzugehen.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="survey-form">
        {/* Kundendaten */}
        <div className="form-section">
          <h3>
            <Mail className="section-icon" />
            Ihre Kontaktdaten
          </h3>
          <div className="form-row">
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                value={formData.customer_name}
                onChange={(e) => handleInputChange('customer_name', e.target.value)}
                placeholder="Ihr vollständiger Name"
                required
              />
            </div>
            <div className="form-group">
              <label>E-Mail Adresse *</label>
              <input
                type="email"
                value={formData.customer_email}
                onChange={(e) => handleInputChange('customer_email', e.target.value)}
                placeholder="ihre.email@beispiel.de"
                required
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Unternehmen</label>
              <input
                type="text"
                value={formData.company_name}
                onChange={(e) => handleInputChange('company_name', e.target.value)}
                placeholder="Name Ihres Unternehmens"
              />
            </div>
            <div className="form-group">
              <label>Position / Funktion im Unternehmen</label>
              <input
                type="text"
                value={formData.job_position}
                onChange={(e) => handleInputChange('job_position', e.target.value)}
                placeholder="Ihre Position oder Funktion"
              />
            </div>
          </div>
          <div className="form-group">
            <label>Bestellnummer (optional)</label>
            <input
              type="text"
              value={formData.order_number}
              onChange={(e) => handleInputChange('order_number', e.target.value)}
              placeholder="Falls Ihnen eine Bestellnummer vorliegt"
            />
          </div>
        </div>

        {/* 1. Allgemeine Zufriedenheit */}
        <div className="form-section">
          <h3>
            <Star className="section-icon" />
            1. Allgemeine Zufriedenheit
          </h3>
          
          <div className="rating-group">
            <label>Wie zufrieden sind Sie insgesamt mit unserer Zusammenarbeit?</label>
            <StarRating
              value={formData.overall_satisfaction_rating}
              onChange={(value) => handleInputChange('overall_satisfaction_rating', value)}
              labels={satisfactionLabels}
            />
            <div className="rating-explanation">1 = sehr unzufrieden, 5 = sehr zufrieden</div>
          </div>

          <div className="rating-group">
            <label>Würden Sie uns an Kollegen oder Geschäftspartner weiterempfehlen?</label>
            <NPSRating
              value={formData.recommendation_rating}
              onChange={(value) => handleInputChange('recommendation_rating', value)}
            />
            <div className="rating-explanation">Skala 0–10 (0 = sehr unwahrscheinlich, 10 = sehr wahrscheinlich)</div>
          </div>
        </div>

        {/* 2. Produkte & Sortiment */}
        <div className="form-section">
          <h3>
            <Target className="section-icon" />
            2. Produkte & Sortiment
          </h3>
          
          <div className="rating-group">
            <label>Wie bewerten Sie die Qualität unserer Produkte im Vergleich zu anderen Lieferanten?</label>
            <StarRating
              value={formData.product_quality_comparison_rating}
              onChange={(value) => handleInputChange('product_quality_comparison_rating', value)}
              labels={qualityComparisonLabels}
            />
            <div className="rating-explanation">1 = schlechter, 5 = deutlich besser</div>
          </div>

          <div className="radio-group">
            <label>Entspricht unser Sortiment Ihren aktuellen Bedürfnissen?</label>
            <div className="radio-options">
              <div className="radio-option">
                <input
                  type="radio"
                  id="range_ja"
                  name="product_range_satisfaction"
                  value="ja"
                  checked={formData.product_range_satisfaction === 'ja'}
                  onChange={(e) => handleInputChange('product_range_satisfaction', e.target.value)}
                />
                <label htmlFor="range_ja">Ja</label>
              </div>
              <div className="radio-option">
                <input
                  type="radio"
                  id="range_teilweise"
                  name="product_range_satisfaction"
                  value="teilweise"
                  checked={formData.product_range_satisfaction === 'teilweise'}
                  onChange={(e) => handleInputChange('product_range_satisfaction', e.target.value)}
                />
                <label htmlFor="range_teilweise">Teilweise</label>
              </div>
              <div className="radio-option">
                <input
                  type="radio"
                  id="range_nein"
                  name="product_range_satisfaction"
                  value="nein"
                  checked={formData.product_range_satisfaction === 'nein'}
                  onChange={(e) => handleInputChange('product_range_satisfaction', e.target.value)}
                />
                <label htmlFor="range_nein">Nein</label>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>Welche zusätzlichen Produkte oder Varianten würden Sie sich wünschen?</label>
            <textarea
              value={formData.product_wishes}
              onChange={(e) => handleInputChange('product_wishes', e.target.value)}
              placeholder="Beschreiben Sie gerne, welche Produkte oder Varianten Sie vermissen..."
              rows={3}
            />
          </div>
        </div>

        {/* 3. Preis & Konditionen */}
        <div className="form-section">
          <h3>
            <Users className="section-icon" />
            3. Preis & Konditionen
          </h3>
          
          <div className="rating-group">
            <label>Wie beurteilen Sie das Preis-Leistungs-Verhältnis?</label>
            <StarRating
              value={formData.price_performance_rating}
              onChange={(value) => handleInputChange('price_performance_rating', value)}
              labels={pricePerformanceLabels}
            />
            <div className="rating-explanation">1 = sehr schlecht, 5 = sehr gut</div>
          </div>

          <div className="radio-group">
            <label>Sind unsere Liefer- und Zahlungskonditionen für Sie passend?</label>
            <div className="radio-options">
              <div className="radio-option">
                <input
                  type="radio"
                  id="conditions_ja"
                  name="delivery_payment_conditions"
                  value="ja"
                  checked={formData.delivery_payment_conditions === 'ja'}
                  onChange={(e) => handleInputChange('delivery_payment_conditions', e.target.value)}
                />
                <label htmlFor="conditions_ja">Ja</label>
              </div>
              <div className="radio-option">
                <input
                  type="radio"
                  id="conditions_teilweise"
                  name="delivery_payment_conditions"
                  value="teilweise"
                  checked={formData.delivery_payment_conditions === 'teilweise'}
                  onChange={(e) => handleInputChange('delivery_payment_conditions', e.target.value)}
                />
                <label htmlFor="conditions_teilweise">Teilweise</label>
              </div>
              <div className="radio-option">
                <input
                  type="radio"
                  id="conditions_nein"
                  name="delivery_payment_conditions"
                  value="nein"
                  checked={formData.delivery_payment_conditions === 'nein'}
                  onChange={(e) => handleInputChange('delivery_payment_conditions', e.target.value)}
                />
                <label htmlFor="conditions_nein">Nein</label>
              </div>
            </div>
          </div>
        </div>

        {/* 4. Lieferung & Logistik */}
        <div className="form-section">
          <h3>
            <Truck className="section-icon" />
            4. Lieferung & Logistik
          </h3>
          
          <div className="rating-group">
            <label>Wie zuverlässig sind unsere Lieferzeiten aus Ihrer Sicht?</label>
            <StarRating
              value={formData.delivery_reliability_rating}
              onChange={(value) => handleInputChange('delivery_reliability_rating', value)}
              labels={reliabilityLabels}
            />
            <div className="rating-explanation">1 = sehr unzuverlässig, 5 = sehr zuverlässig</div>
          </div>

          <div className="rating-group">
            <label>Wie bewerten Sie die Vollständigkeit und Unversehrtheit der Lieferungen?</label>
            <StarRating
              value={formData.delivery_completeness_rating}
              onChange={(value) => handleInputChange('delivery_completeness_rating', value)}
              labels={completenessLabels}
            />
            <div className="rating-explanation">1 = sehr schlecht, 5 = sehr gut</div>
          </div>
        </div>

        {/* 5. Service & Kommunikation */}
        <div className="form-section">
          <h3>
            <Phone className="section-icon" />
            5. Service & Kommunikation
          </h3>
          
          <div className="rating-group">
            <label>Wie kompetent empfinden Sie die Beratung durch unser Team?</label>
            <StarRating
              value={formData.consultation_competence_rating}
              onChange={(value) => handleInputChange('consultation_competence_rating', value)}
              labels={competenceLabels}
            />
            <div className="rating-explanation">1 = sehr inkompetent, 5 = sehr kompetent</div>
          </div>

          <div className="rating-group">
            <label>Wie zufrieden sind Sie mit der digitalen Kommunikation (z. B. Bestellbestätigungen, Dokumente, Online-Tools)?</label>
            <StarRating
              value={formData.digital_communication_rating}
              onChange={(value) => handleInputChange('digital_communication_rating', value)}
              labels={digitalLabels}
            />
            <div className="rating-explanation">1 = sehr unzufrieden, 5 = sehr zufrieden</div>
          </div>
        </div>

        {/* 6. Offene Anmerkungen */}
        <div className="form-section">
          <h3>
            <Target className="section-icon" />
            6. Offene Anmerkungen
          </h3>
          
          <div className="form-group">
            <label>Welche drei Dinge sollten wir beibehalten?</label>
            <textarea
              value={formData.keep_suggestions}
              onChange={(e) => handleInputChange('keep_suggestions', e.target.value)}
              placeholder="Was funktioniert gut und sollte beibehalten werden..."
              rows={3}
            />
          </div>

          <div className="form-group">
            <label>Welche drei Dinge sollten wir verbessern?</label>
            <textarea
              value={formData.improvement_suggestions}
              onChange={(e) => handleInputChange('improvement_suggestions', e.target.value)}
              placeholder="Was könnte besser gemacht werden..."
              rows={3}
            />
          </div>
        </div>

        {/* DSGVO Confirmation */}
        <div className="form-section dsgvo-section">
          <div className="dsgvo-confirmation">
            <p className="dsgvo-text">
              🛡️ <strong>Datenschutz:</strong> Mit dem Absenden bestätigen Sie erneut, dass Sie mit der Verarbeitung 
              Ihrer Daten gemäß unserer Datenschutzerklärung einverstanden sind. Sie können Ihre 
              Einwilligung jederzeit per E-Mail an <a href="mailto:datenschutz@eurosill.com">datenschutz@eurosill.com</a> widerrufen.
            </p>
          </div>
        </div>

        {error && (
          <div className="error-message">
            <AlertCircle size={20} />
            {error}
          </div>
        )}

        <div className="form-actions">
          <button
            type="submit"
            disabled={isSubmitting}
            className="submit-button"
          >
            {isSubmitting ? (
              <>
                <div className="loading-spinner"></div>
                Wird übermittelt...
              </>
            ) : (
              <>
                <Send size={20} />
                Umfrage absenden
              </>
            )}
          </button>
        </div>
      </form>

      <div className="survey-footer">
        <p>© 2025 <span className="footer-highlight">Eurosill GmbH</span> - Ihre Meinung zählt!</p>
      </div>
    </div>
  );
};

function App() {
  const [currentView, setCurrentView] = useState('survey'); // 'survey', 'admin-login', 'admin'
  const [adminUser, setAdminUser] = useState(null);
  const [showPrivacyNotice, setShowPrivacyNotice] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  // Check privacy consent on app load
  useEffect(() => {
    const consent = localStorage.getItem('privacy_consent_accepted');
    if (consent === 'true') {
      setPrivacyAccepted(true);
    } else {
      // Show privacy notice after short delay for better UX
      setTimeout(() => setShowPrivacyNotice(true), 1000);
    }
  }, []);

  // Check for existing session on app load
  useEffect(() => {
    const checkExistingSession = async () => {
      const token = localStorage.getItem('admin_session_token');
      const username = localStorage.getItem('admin_username');
      
      if (token && username) {
        try {
          // Verify session is still valid
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const response = await axios.get(`${API}/admin/verify-session`);
          
          if (response.data.success) {
            setAdminUser({ username: response.data.username, token });
            if (currentView === 'admin-login') {
              setCurrentView('admin');
            }
          } else {
            // Session invalid, clear storage
            localStorage.removeItem('admin_session_token');
            localStorage.removeItem('admin_username');
            localStorage.removeItem('admin_expires_at');
            delete axios.defaults.headers.common['Authorization'];
          }
        } catch (err) {
          // Session verification failed, clear storage
          localStorage.removeItem('admin_session_token');
          localStorage.removeItem('admin_username');
          localStorage.removeItem('admin_expires_at');
          delete axios.defaults.headers.common['Authorization'];
        }
      }
    };

    checkExistingSession();
  }, [currentView]);

  const handlePrivacyAccept = () => {
    localStorage.setItem('privacy_consent_accepted', 'true');
    localStorage.setItem('privacy_consent_date', new Date().toISOString());
    setPrivacyAccepted(true);
    setShowPrivacyNotice(false);
  };

  const handlePrivacyDecline = () => {
    localStorage.removeItem('privacy_consent_accepted');
    localStorage.removeItem('privacy_consent_date');
    setShowPrivacyNotice(false);
    // Redirect to company website or show message
    alert('Ohne Zustimmung zur Datenschutzerklärung können Sie leider nicht an der Umfrage teilnehmen. Sie werden zu unserer Website weitergeleitet.');
    window.location.href = 'https://www.eurosill.com';
  };

  const handleAdminLogin = (userData) => {
    setAdminUser(userData);
    setCurrentView('admin');
  };

  const handleAdminLogout = () => {
    setAdminUser(null);
    setCurrentView('survey');
  };

  const handleGoToAdminLogin = () => {
    setCurrentView('admin-login');
  };

  // Admin Login View
  if (currentView === 'admin-login') {
    return (
      <div className="App">
        <div className="view-switcher">
          <button 
            onClick={() => setCurrentView('survey')}
            className="switch-view-btn"
          >
            <ArrowLeft size={16} />
            Zurück zur Umfrage
          </button>
        </div>
        <AdminLogin onLoginSuccess={handleAdminLogin} />
      </div>
    );
  }

  // Admin Dashboard View (with authentication check)
  if (currentView === 'admin' && adminUser) {
    return (
      <div className="App">
        <AdminDashboard onLogout={handleAdminLogout} username={adminUser.username} />
      </div>
    );
  }

  // Survey View (default)
  return (
    <div className="App">
      <div className="view-switcher">
        <button 
          onClick={handleGoToAdminLogin}
          className="switch-view-btn admin"
        >
          <BarChart3 size={16} />
          Admin Login
        </button>
      </div>
      
      {/* Survey Form - only show if privacy accepted */}
      {privacyAccepted ? (
        <SurveyForm />
      ) : (
        <div className="privacy-pending">
          <div className="privacy-pending-content">
            <h2>Datenschutz-Zustimmung erforderlich</h2>
            <p>Bitte stimmen Sie der Datenschutzerklärung zu, um an der Umfrage teilzunehmen.</p>
            <button 
              onClick={() => setShowPrivacyNotice(true)} 
              className="show-privacy-btn"
            >
              🛡️ Datenschutzerklärung anzeigen
            </button>
          </div>
        </div>
      )}

      {/* Privacy Notice Modal */}
      <PrivacyNotice
        isVisible={showPrivacyNotice}
        onAccept={handlePrivacyAccept}
        onDecline={handlePrivacyDecline}
      />
    </div>
  );
}

export default App;